import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import { CancelMinor } from '@shopify/polaris-icons'
import { MenuActionItem } from 'src/interfaces/ui'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { ActionButton } from 'src/components/shared/ActionButton'
import tw from 'twin.macro'
import { DeleteMCPayment } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { useDispatch } from 'react-redux'
import { PaymentStatus } from 'src/constants/payment/enums'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { showToast } from 'src/utils/toast'
import { addS } from 'src/utils/baseUtils'
import { UserPermission } from 'src/constants/user'
import { handleApiError } from 'src/utils/errorHandlers'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { MCPaymentsTableContext } from '../../payments-table/MCPaymentsTable.context'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'

const MCPaymentsBatchActionButtonComponent = () => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const { reload } = useContext(ReloadWrapperContext)
  const { selectedMCPayments } = useContext(MCPaymentsTableContext)

  const selectedMCPaymentsToDelete = useMemo(
    () =>
      selectedMCPayments.filter((item) =>
        [PaymentStatus.NotPaid, PaymentStatus.RequiresFunding].includes(item.status),
      ),
    [selectedMCPayments],
  )

  const isAllSelectedMCPaymentsDeletable = useMemo(() => {
    if (!selectedMCPayments.length) {
      return false
    }

    return selectedMCPayments.every((item) =>
      [PaymentStatus.NotPaid, PaymentStatus.RequiresFunding].includes(item.status),
    )
  }, [selectedMCPayments])

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const isOpen = Boolean(anchorEl)

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false)
  }, [])

  const actionItems: MenuActionItem[] = useMemo(() => {
    if (!selectedMCPaymentsToDelete.length || !hasPermission(UserPermission.PaymentsDelete)) {
      return []
    }

    return [
      {
        label: 'Delete MCPayments',
        icon: <CancelMinor width={20} height={20} />,
        onClick: () => {
          if (!selectedMCPaymentsToDelete.length) {
            return
          }

          handleOpenConfirm()
          handleClose()
        },
      },
    ]
  }, [selectedMCPaymentsToDelete, hasPermission])

  const handleDeleteMCPayment = useCallback(async () => {
    if (!selectedMCPaymentsToDelete.length) {
      return
    }

    try {
      await createAsyncAction(
        dispatch,
        DeleteMCPayment.request({
          paymentIds: selectedMCPaymentsToDelete.map((item) => item.id),
        }),
      )

      reload()

      showToast('MCPayment is deleted.')
    } catch (err) {
      handleApiError(err)
    }

    handleCloseConfirm()
  }, [selectedMCPaymentsToDelete, reload])

  return (
    <>
      <ActionButton
        className={tw`px-4`}
        selected={!!anchorEl}
        color="white"
        text="Actions"
        disabled={
          !isAllSelectedMCPaymentsDeletable || !hasPermission(UserPermission.PaymentsDelete)
        }
        onClick={handleClick}
      />
      <ActionMenu
        anchorEl={anchorEl}
        isOpen={isOpen}
        items={actionItems}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
      />
      <SensitiveActionModal
        isOpen={isConfirmOpen}
        title={`Delete ${addS(selectedMCPaymentsToDelete.length, 'payment')}`}
        text={`Are you sure you want to delete ${selectedMCPaymentsToDelete.length} ${addS(
          selectedMCPaymentsToDelete.length,
          'payment',
        )}?`}
        submitLabel="Delete"
        onClose={handleCloseConfirm}
        onSubmit={handleDeleteMCPayment}
      />
    </>
  )
}

export const MCPaymentsBatchActionButton = memo(MCPaymentsBatchActionButtonComponent)
